import { useFeature } from '../auth/features';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    MessageSquare, Send, Loader2, CheckCircle2, ThumbsUp, ThumbsDown,
    Bug, Lightbulb, HelpCircle, Clock, MessageCircle,
} from 'lucide-react';
import { authService, feedbackService } from '../services/api';
import { toast } from 'sonner';
import { clsx } from 'clsx';

interface FeedbackItem {
    id: number;
    username: string | null;
    feedback_type: string;
    chain: string | null;
    address: string | null;
    is_accurate: boolean | null;
    comments: string | null;
    status: string;
    admin_response: string | null;
    responded_at: string | null;
    responded_by: string | null;
    created_at: string;
}

const FEEDBACK_TYPES = [
    { value: 'general', label: 'General', icon: MessageSquare },
    { value: 'bug', label: 'Bug', icon: Bug },
    { value: 'feature', label: 'Idea', icon: Lightbulb },
    { value: 'question', label: 'Question', icon: HelpCircle },
] as const;

const FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'open', label: 'Open' },
    { value: 'responded', label: 'Resolved' },
] as const;

const FEEDBACK_MAX_CHARS = 2000;
const FEEDBACK_AMBER_AT = 1800;

function formatDate(d: string) {
    try {
        return new Date(d).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return d;
    }
}

function TypeIcon({ type }: { type: string }) {
    switch (type) {
        case 'bug': return <Bug className="w-3.5 h-3.5 text-muted-foreground" />;
        case 'feature': return <Lightbulb className="w-3.5 h-3.5 text-muted-foreground" />;
        case 'question': return <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" />;
        case 'investigation': return <ThumbsUp className="w-3.5 h-3.5 text-muted-foreground" />;
        default: return <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />;
    }
}

function StatusBadge({ status }: { status: string }) {
    if (status === 'responded') {
        return (
            <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-md bg-muted text-muted-foreground border border-border">
                <CheckCircle2 className="w-3 h-3" /> Resolved
            </span>
        );
    }
    return (
        <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-md bg-primary/10 text-primary border border-primary/20">
            <Clock className="w-3 h-3" /> Open
        </span>
    );
}

export function Feedback() {
    const [items, setItems] = useState<FeedbackItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string>('all');
    const [role, setRole] = useState<string | null>(null);
    const [view, setView] = useState<'mine' | 'inbox'>('mine');

    const [feedbackType, setFeedbackType] = useState<string>('general');
    const [comments, setComments] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [replyDrafts, setReplyDrafts] = useState<Record<number, string>>({});
    const [replyingId, setReplyingId] = useState<number | null>(null);

    const composeRef = useRef<HTMLTextAreaElement>(null);
    // Respond/inbox is require_admin on the API.
    const isAdmin = useFeature('admin.tools');

    useEffect(() => {
        authService.getMe().then((me: { role?: string }) => {
            setRole(me?.role ?? null);
        }).catch(() => setRole(null));
    }, []);

    const loadFeedback = useCallback(async () => {
        setLoading(true);
        try {
            const statusParam = filter === 'all' ? undefined : filter;
            const data = await feedbackService.list(statusParam);
            setItems(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to load feedback:', error);
            toast.error('Failed to load notes');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        loadFeedback();
    }, [loadFeedback]);

    const handleSubmit = async () => {
        if (!comments.trim()) {
            toast.error('Please enter your note');
            return;
        }
        setSubmitting(true);
        try {
            await feedbackService.submitGeneral(feedbackType, comments.trim());
            toast.success('Submitted — admins are notified. You will get a reply here and in Notifications.');
            setComments('');
            setFeedbackType('general');
            setView('mine');
            await loadFeedback();
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            toast.error('Failed to submit note');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReply = async (itemId: number) => {
        const text = (replyDrafts[itemId] || '').trim();
        if (!text) {
            toast.error('Enter a reply before resolving');
            return;
        }
        setReplyingId(itemId);
        try {
            await feedbackService.respond(itemId, text);
            toast.success('Resolved — officer will be notified');
            setReplyDrafts(prev => {
                const next = { ...prev };
                delete next[itemId];
                return next;
            });
            await loadFeedback();
        } catch (error) {
            console.error('Failed to reply:', error);
            toast.error('Failed to send reply');
        } finally {
            setReplyingId(null);
        }
    };

    const focusCompose = () => {
        composeRef.current?.focus();
        composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    const openItems = items.filter(i => i.status !== 'responded');

    // Inbox: open notes across officers by default; Resolved filter shows closed ones.
    const listItems = (() => {
        if (!(isAdmin && view === 'inbox')) return items;
        if (filter === 'responded') return items.filter(i => i.status === 'responded');
        if (filter === 'open' || filter === 'all') return openItems;
        return items;
    })();

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                    <MessageCircle className="w-6 h-6 text-primary" />
                    Feedback
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Send a note about bugs, ideas, or questions. Admins are notified and can resolve with a reply.
                </p>
            </div>

            {isAdmin && (
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setView('mine')}
                        className={clsx(
                            'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                            view === 'mine'
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card text-muted-foreground border-border hover:bg-muted'
                        )}
                    >
                        Compose &amp; my notes
                    </button>
                    <button
                        type="button"
                        onClick={() => { setView('inbox'); setFilter('open'); }}
                        className={clsx(
                            'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                            view === 'inbox'
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card text-muted-foreground border-border hover:bg-muted'
                        )}
                    >
                        Inbox{openItems.length > 0 ? ` (${openItems.length} open)` : ''}
                    </button>
                </div>
            )}

            {(!isAdmin || view === 'mine') && (
                <div className="bg-card border border-border rounded-lg p-5 space-y-4">
                    <h2 className="text-sm font-semibold text-foreground">Send a note</h2>

                    <div className="flex flex-wrap gap-2">
                        {FEEDBACK_TYPES.map(ft => (
                            <button
                                key={ft.value}
                                type="button"
                                onClick={() => setFeedbackType(ft.value)}
                                className={clsx(
                                    'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition-colors font-medium',
                                    feedbackType === ft.value
                                        ? 'bg-primary/10 text-primary border-primary/30'
                                        : 'bg-card text-muted-foreground border-border hover:bg-muted'
                                )}
                            >
                                <ft.icon className="w-3.5 h-3.5" />
                                {ft.label}
                            </button>
                        ))}
                    </div>

                    <textarea
                        ref={composeRef}
                        value={comments}
                        onChange={e => setComments(e.target.value.slice(0, FEEDBACK_MAX_CHARS))}
                        maxLength={FEEDBACK_MAX_CHARS}
                        placeholder={
                            feedbackType === 'bug'
                                ? 'Describe what happened, what you expected, and steps to reproduce...'
                                : feedbackType === 'feature'
                                    ? 'Describe the idea and why it would help investigations...'
                                    : feedbackType === 'question'
                                        ? 'What would you like to know?'
                                        : 'Share your thoughts or observations...'
                        }
                        className="w-full text-sm p-3 bg-background border border-input rounded-md resize-none h-32 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />

                    <div className="flex items-center justify-between gap-3">
                        <span
                            className={clsx(
                                'text-xs tabular-nums',
                                comments.length >= FEEDBACK_MAX_CHARS
                                    ? 'text-destructive font-medium'
                                    : comments.length >= FEEDBACK_AMBER_AT
                                        ? 'text-muted-foreground font-medium'
                                        : 'text-muted-foreground'
                            )}
                        >
                            {comments.length} / {FEEDBACK_MAX_CHARS}
                        </span>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || !comments.trim()}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Submit note
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold text-foreground">
                        {isAdmin && view === 'inbox' ? 'Inbox' : 'Your notes'}
                    </h2>
                    <div className="flex items-center gap-1">
                        {FILTERS.map(f => (
                            <button
                                key={f.value}
                                type="button"
                                onClick={() => setFilter(f.value)}
                                className={clsx(
                                    'px-2.5 py-1 text-xs rounded-md transition-colors',
                                    filter === f.value
                                        ? 'bg-primary/10 text-primary font-medium'
                                        : 'text-muted-foreground hover:bg-muted'
                                )}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                ) : listItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-14 text-center border border-dashed border-border rounded-lg bg-card/50">
                        <MessageCircle className="w-8 h-8 text-muted-foreground/60 mb-3" />
                        <p className="text-sm font-medium text-foreground">
                            {isAdmin && view === 'inbox'
                                ? (filter === 'responded' ? 'No resolved notes yet.' : 'Inbox is clear — no open notes.')
                                : filter === 'all'
                                    ? 'No notes yet.'
                                    : filter === 'open'
                                        ? 'No open notes.'
                                        : 'No resolved notes.'}
                        </p>
                        {(!isAdmin || view === 'mine') && filter === 'all' && (
                            <button
                                type="button"
                                onClick={focusCompose}
                                className="mt-3 text-sm text-primary hover:underline"
                            >
                                Send your first note
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {listItems.map(item => (
                            <div key={item.id} className="bg-card border border-border rounded-lg p-4 space-y-2">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <TypeIcon type={item.feedback_type} />
                                        <span className="text-xs font-medium text-foreground capitalize">
                                            {item.feedback_type === 'feature' ? 'idea' : item.feedback_type}
                                        </span>
                                        {isAdmin && item.username && (
                                            <span className="text-[10px] text-muted-foreground truncate">
                                                from {item.username}
                                            </span>
                                        )}
                                        {item.feedback_type === 'investigation' && item.is_accurate !== null && (
                                            item.is_accurate
                                                ? <ThumbsUp className="w-3 h-3 text-muted-foreground" />
                                                : <ThumbsDown className="w-3 h-3 text-muted-foreground" />
                                        )}
                                        {item.chain && item.address && (
                                            <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                                {item.chain}: {item.address.slice(0, 8)}…{item.address.slice(-6)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <StatusBadge status={item.status} />
                                        <span className="text-[10px] text-muted-foreground">{formatDate(item.created_at)}</span>
                                    </div>
                                </div>

                                {item.comments && (
                                    <p className="text-sm text-foreground/80 bg-muted/40 rounded p-2.5 whitespace-pre-wrap break-words">
                                        {item.comments}
                                    </p>
                                )}

                                {item.admin_response && (
                                    <div className="border-l-2 border-primary/40 pl-3 ml-1">
                                        <div className="text-[10px] text-muted-foreground mb-0.5">
                                            Reply from {item.responded_by || 'admin'}
                                            {item.responded_at ? ` · ${formatDate(item.responded_at)}` : ''}
                                        </div>
                                        <p className="text-sm text-foreground/80">{item.admin_response}</p>
                                    </div>
                                )}

                                {isAdmin && view === 'inbox' && item.status !== 'responded' && (
                                    <div className="space-y-2 pt-1 border-t border-border">
                                        <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            Reply (resolves this note)
                                        </label>
                                        <textarea
                                            value={replyDrafts[item.id] || ''}
                                            onChange={e => setReplyDrafts(prev => ({
                                                ...prev,
                                                [item.id]: e.target.value.slice(0, FEEDBACK_MAX_CHARS),
                                            }))}
                                            placeholder="Explain the fix, next steps, or answer the question…"
                                            className="w-full text-sm p-2.5 bg-background border border-input rounded-md resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        />
                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => handleReply(item.id)}
                                                disabled={replyingId === item.id || !(replyDrafts[item.id] || '').trim()}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 disabled:opacity-50"
                                            >
                                                {replyingId === item.id
                                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    : <CheckCircle2 className="w-3.5 h-3.5" />}
                                                Resolve with reply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
